/**
 * Entry point for /users/csv-ringgroup-membership — bulk add/remove 8x8 Ring
 * Group members from a CSV. Modelled on CreateCSV.jsx: reuses the same
 * upload/verify/submit/results scaffolding (CSVTemplateView, VerifyCSVView,
 * VerifyCSVTableView, APILoadingView, APIResultsView) as the Users CSV flows,
 * paired with its own verify/submit/template hooks since the row shape here
 * is (Ring Group, Member, Action) rather than a user record.
 */
import React from 'react';
import { ChakraProvider, Box } from '@chakra-ui/react';
import chakratheme from '../../../../chakratheme';
import { useSelector } from 'react-redux';
import { Error } from 'components/index.js';
import { nullError } from 'components/error.js';
import appConfig from '../../../../config.json';

import useCSVRingGroupMembership from './useCSVRingGroupMembership.js';
import useCSVRingGroupMembershipBulkUpdate from './useCSVRingGroupMembershipBulkUpdate.js';
import useCSVRingGroupMembershipTemplate from './useCSVRingGroupMembershipTemplate.js';

import CSVTemplateView from './CSVTemplateView.jsx';
import VerifyCSVView from './VerifyCSVView.jsx';
import VerifyCSVTableView from './VerifyCSVTableView.jsx';
import APILoadingView from './APILoadingView.jsx';
import APIResultsView from './APIResultsView.jsx';

import './reset.css';

const DOWNLOAD_CSV_TEMPLATE = 'DOWNLOAD_CSV_TEMPLATE';
const LOADING_CSV_VALIDATION = 'LOADING_CSV_VALIDATION';
const CSV_VALIDATION_RESULTS = 'CSV_VALIDATION_RESULTS';
const API_CALLS_PROGRESS = 'API_CALLS_PROGRESS';
const API_CALLS_RESULTS = 'API_CALLS_RESULTS';
const CSV_DATA_NOT_FOUND = 'CSV_DATA_NOT_FOUND';
const CSV_LIMIT_REACHED = 'CSV_LIMIT_REACHED';

const renderErrors = (rowData) => {
    if (Array.isArray(rowData.errors) && rowData.errors.length > 0) {
        return (
            <span style={{ whiteSpace: 'pre-line' }}>
                {rowData.errors.join('\n')}
            </span>
        );
    }
    return null;
};

// Columns shown in the verification table for this flow
const passedColumns = [
    { title: 'Ring Group Name', field: 'ringGroupName' },
    { title: 'Member UPN', field: 'memberUPN' },
    { title: 'Action', field: 'action' },
    { title: 'Verification Status', field: 'verificationStatus' },
];

const failedColumns = [
    { title: 'Ring Group Name', field: 'ringGroupName' },
    { title: 'Member UPN', field: 'memberUPN' },
    { title: 'Action', field: 'action' },
    {
        title: 'Failure Reasons',
        field: 'errors',
        width: '40%',
        render: renderErrors,
    },
];

const succeedAPIColumns = [
    { title: 'Ring Group Name', field: 'ringGroupName' },
    { title: 'Member UPN', field: 'memberUPN' },
    { title: 'Action', field: 'action' },
];

const failedAPIColumns = [
    { title: 'Ring Group Name', field: 'ringGroupName' },
    { title: 'Member UPN', field: 'memberUPN' },
    { title: 'Action', field: 'action' },
    { title: 'Failure Reasons', field: 'apiErrors' },
];

function useRingGroupMembershipCSVDisplayControl() {
    const [viewVerificationResults, setViewVerificationResults] =
        React.useState(false);
    const [viewAPIResults, setViewAPIResults] = React.useState(false);

    const {
        handleUploadUsersData,
        uploadData,
        verificationChecks,
        loading: verificationLoading,
        error: verificationErrors,
        payloads,
        verificationResult,
        handleReset: handleResetCSV,
        handleExportInvalidData,
        csvLimit,
    } = useCSVRingGroupMembership();

    const {
        handlePayloadChange,
        loading: apiLoading,
        counter: completionCounter,
        errorCounter,
        error: apiErrors,
        succeed: apiSucceed,
        handleExportFailedData,
        handleReset: handleResetBulkUpdate,
        total,
    } = useCSVRingGroupMembershipBulkUpdate();

    const handleViewVerificationResults = () =>
        setViewVerificationResults(true);
    const handleViewAPIResults = () => setViewAPIResults(true);

    const handleStartOver = () => {
        setViewVerificationResults(false);
        setViewAPIResults(false);
        handleResetCSV();
        handleResetBulkUpdate();
        window.location.reload();
    };

    const handleCurrentDisplay = () => {
        if (verificationErrors) return CSV_DATA_NOT_FOUND;
        if (csvLimit) return CSV_LIMIT_REACHED;
        if (!uploadData) return DOWNLOAD_CSV_TEMPLATE;
        if (viewAPIResults) return API_CALLS_RESULTS;
        if (apiLoading) return API_CALLS_PROGRESS;
        if (viewVerificationResults) return CSV_VALIDATION_RESULTS;
        if (verificationLoading) return LOADING_CSV_VALIDATION;
    };

    const handleCancelValidationTable = () => {
        handleResetCSV();
        setViewVerificationResults(false);
    };

    const handleStartAPICall = () => {
        handlePayloadChange(payloads);
        setViewVerificationResults(false);
    };

    return {
        handleUploadUsersData,
        handleCurrentDisplay,
        verificationChecks,
        verificationErrors,
        handleViewVerificationResults,
        verificationResult,
        handleCancel: handleCancelValidationTable,
        handleImport: handleStartAPICall,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
        csvLimit,
    };
}

export default function RingGroupMembershipCSV() {
    const {
        handleUploadUsersData,
        handleCurrentDisplay,
        verificationChecks,
        handleViewVerificationResults,
        verificationResult,
        handleCancel,
        verificationErrors,
        handleImport,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
        csvLimit,
    } = useRingGroupMembershipCSVDisplayControl();

    const { handleDownloadTemplate, loading: templateLoading } =
        useCSVRingGroupMembershipTemplate();

    const { currentCompany } = useSelector((state) => state.navigationLists);

    if (!currentCompany) {
        return <Error error={nullError} />;
    }

    const currentDisplay = handleCurrentDisplay();

    return (
        <ChakraProvider theme={chakratheme} resetCSS={true}>
            <Box id="csv-ringgroup-membership">
                {(currentDisplay === DOWNLOAD_CSV_TEMPLATE ||
                    currentDisplay === CSV_LIMIT_REACHED) && (
                    <CSVTemplateView
                        handleUploadUsersData={handleUploadUsersData}
                        enablePlatformSelection={false}
                        isUserPage={true}
                        downloadCSVText="Download Template"
                        header="CSV Update - Ring Group Membership"
                        step1Text="Step 1 - Download the blank CSV template"
                        step2Text="Step 2 - Fill in Add/Remove rows and upload"
                        instructionsHeader="Instructions"
                        handleDownloadTemplate={handleDownloadTemplate}
                        loading={templateLoading}
                        bgColor="white"
                        instructionsBody={[
                            `CSV upload is limited to a maximum of ${appConfig.csvLimit} rows per upload.`,
                            'Required columns are: Ring Group Name, Member UPN, Action.',
                            'Ring Group Name must match an existing Ring Group’s name exactly (case-insensitive).',
                            'Member UPN must match an existing 8x8 platform user’s UPN exactly (case-insensitive).',
                            'Action accepts "Add" or "Remove" only.',
                            'A member being added must already have a phone number assigned, and cannot itself be a Ring Group.',
                            'Rows that would be a no-op (adding an existing member, or removing a non-member) are ignored and will not trigger any change.',
                            'You are responsible for the changes you are about to make through this data import.',
                        ]}
                        csvLimit={csvLimit}
                    />
                )}
                {currentDisplay === LOADING_CSV_VALIDATION && (
                    <VerifyCSVView
                        handleViewVerificationResults={
                            handleViewVerificationResults
                        }
                        verificationChecks={verificationChecks}
                    />
                )}
                {currentDisplay === CSV_VALIDATION_RESULTS && (
                    <VerifyCSVTableView
                        verificationResult={verificationResult}
                        handleCancel={handleCancel}
                        handleImport={handleImport}
                        handleExportInvalidData={handleExportInvalidData}
                        handleStartOver={handleStartOver}
                        passedColumns={passedColumns}
                        failedColumns={failedColumns}
                    />
                )}
                {currentDisplay === API_CALLS_PROGRESS && (
                    <APILoadingView
                        completionCounter={completionCounter}
                        payloads={payloads}
                        verificationResult={verificationResult}
                        errorCounter={errorCounter}
                        handleViewAPIResults={handleViewAPIResults}
                        total={total}
                    />
                )}
                {currentDisplay === API_CALLS_RESULTS && (
                    <APIResultsView
                        error={apiErrors}
                        succeed={apiSucceed}
                        succeedAPIColumns={succeedAPIColumns}
                        failedAPIColumns={failedAPIColumns}
                        handleExportFailedData={handleExportFailedData}
                        handleDone={handleStartOver}
                    />
                )}
                {currentDisplay === CSV_DATA_NOT_FOUND && (
                    <VerifyCSVView
                        handleStartOver={handleStartOver}
                        errors={verificationErrors}
                    />
                )}
            </Box>
        </ChakraProvider>
    );
}
